import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Routes, Route, NavLink, Navigate } from "react-router-dom";
import {
  getPortalBranding, portalLogin,
  getPortalTickets, getPortalTicket, createPortalTicket, addPortalResponse,
  getPortalDevices, getPortalKBCategories, getPortalKBArticles, getPortalKBArticle,
  getPortalDesktopStatus, getPortalSecureLink, getSecureLinkMeta,
} from "../api";
import {
  Ticket, Monitor, LayoutDashboard, LogOut, Plus, ChevronRight,
  AlertCircle, CheckCircle, Clock, Send, X, Menu, RefreshCw,
  BookOpen, Search, ArrowLeft, Globe, Lock, Eye, Paperclip,
  Download, Apple, MonitorDown,
} from "lucide-react";
import { linkLocalDevice } from "./linkLocalDevice";
import { registerMobileDevice } from "../mobileDevice";
import useIsMobile from "../hooks/useIsMobile";
import { proxyExternalImages } from "../components/HtmlContent";

const API_URL = process.env.REACT_APP_API_URL || "";

// Render a ticket message body. Inbound emails (Outlook, Gmail, …) arrive as full
// HTML carrying their own inline colors (usually black), so we render them on a white
// "email card" the way a dark-mode email client does — legible regardless of the dark
// portal theme. Plain-text / AI bodies render in the portal's normal dark style.
function MessageBody({ text }) {
  if (!text) return null;
  const normalized = String(text).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const isHtml = /<[a-zA-Z][^>]*>/.test(normalized);
  if (isHtml) {
    return (
      <div
        className="portal-email-html"
        style={{ background:"#fff", color:"#1a1a1a", borderRadius:8, padding:"12px 14px",
          lineHeight:1.6, overflowX:"auto", wordBreak:"break-word" }}
        dangerouslySetInnerHTML={{ __html: proxyExternalImages(normalized) }}
      />
    );
  }
  return <div style={{ whiteSpace:"pre-wrap" }}>{normalized}</div>;
}

// =============================================================
//  HELPERS
// =============================================================
const fmtDate   = (d) => d ? new Date(d).toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" }) : "—";
const fmtTime   = (d) => d ? new Date(d).toLocaleString("en-US",  { month:"short", day:"numeric", hour:"numeric", minute:"2-digit" }) : "—";
const timeAgo   = (d) => {
  if (!d) return "—";
  const mins = Math.round((Date.now() - new Date(d)) / 60000);
  if (mins < 60)    return `${mins}m ago`;
  if (mins < 1440)  return `${Math.round(mins/60)}h ago`;
  return `${Math.round(mins/1440)}d ago`;
};

const SLA_COLORS = {
  breach: { bg: "#7f1d1d", color: "#fca5a5", label: "SLA Breached" },
  hold:   { bg: "#78350f", color: "#fcd34d", label: "SLA Hold"     },
  ok:     { bg: "#14532d", color: "#86efac", label: "Within SLA"   },
};

const PRIORITY_COLORS = {
  Critical: "#ef4444",
  High:     "#f97316",
  Medium:   "#eab308",
  Low:      "#22c55e",
};

const STATUS_COLORS = {
  Open:        { bg:"#1e3a5f", color:"#93c5fd" },
  "In Progress":{ bg:"#1e3a2f", color:"#6ee7b7" },
  "On Hold":   { bg:"#3b2f00", color:"#fde68a" },
  Resolved:    { bg:"#14532d", color:"#86efac" },
  Closed:      { bg:"#1e293b", color:"#64748b" },
};

// =============================================================
//  THEMED UI PRIMITIVES  (use branding colors)
// =============================================================
const Badge = ({ label, bg, color }) => (
  <span style={{ padding:"2px 8px", borderRadius:4, fontSize:11, fontWeight:600, background:bg, color }}>{label}</span>
);

// =============================================================
//  PORTAL LOGIN
// =============================================================
function PortalLogin({ branding, onLogin, expectedEmail = "" }) {
  const [email, setEmail]     = useState(expectedEmail || "");
  const [password, setPass]   = useState("");
  const [error, setError]     = useState("");
  const [loading, setLoading] = useState(false);
  const location = window.location;

  const primary  = branding.primary_color || "#4f6fff";
  const bg       = branding.accent_color  || "#0f172a";
  const logo     = branding.logo_url ? `${API_URL}${branding.logo_url}` : null;
  const name     = branding.company_name  || "Support Portal";
  const ssoEnabled = !!branding.sso_enabled;

  // Handle SSO redirect — backend sends ?token=... back to this page
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get("token");
    const ssoError = params.get("error");
    if (ssoError) {
      const msgs = { sso_failed:"Microsoft sign-in was cancelled.", domain_not_found:"Your email domain is not linked to this portal. Contact your admin.", sso_error:"Microsoft sign-in failed. Please try again." };
      setError(msgs[ssoError] || "Sign-in failed. Please try again.");
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    if (ssoToken) {
      try {
        // JWT uses base64url (- and _ instead of + and /). atob() only accepts standard
        // base64, so convert and pad before decoding.
        const b64 = ssoToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
        const padded = b64 + "=".repeat((4 - b64.length % 4) % 4);
        const payload = JSON.parse(atob(padded));
        const contact = { id: payload.id, email: payload.email, clientId: payload.client_id, portalRole: payload.portal_role, firstName: payload.first_name || "", lastName: payload.last_name || "" };
        localStorage.setItem("midtc_portal_token",   ssoToken);
        localStorage.setItem("midtc_portal_contact", JSON.stringify(contact));
        window.history.replaceState({}, "", window.location.pathname);
        linkLocalDevice(ssoToken).catch(() => {}); // best-effort: link this machine to the user
        onLogin(contact);
      } catch { setError("Sign-in token was invalid. Please try again."); }
    }
  }, []);

  const submit = async () => {
    if (!email || !password) { setError("Email and password required"); return; }
    setLoading(true); setError("");
    try {
      const res = await portalLogin(email, password);
      if (res.data.contact.clientId !== branding.client_id) {
        setError("No access for this portal."); setLoading(false); return;
      }
      localStorage.setItem("midtc_portal_token",   res.data.token);
      localStorage.setItem("midtc_portal_contact", JSON.stringify(res.data.contact));
      linkLocalDevice(res.data.token).catch(() => {}); // best-effort: link this machine to the user
      onLogin(res.data.contact);
    } catch (err) {
      setError(err.response?.data?.error || "Login failed");
    }
    setLoading(false);
  };

  const signInWithMicrosoft = () => {
    const hint = expectedEmail ? `&login_hint=${encodeURIComponent(expectedEmail)}` : "";
    window.location.href = `${API_URL}/api/auth/m365/portal/login?return=${encodeURIComponent(window.location.pathname)}${hint}`;
  };

  return (
    <div style={{ minHeight:"100vh", background:bg, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ width:"100%", maxWidth:400 }}>
        <div style={{ textAlign:"center", marginBottom:32 }}>
          {logo
            ? <img src={logo} alt={name} style={{ maxHeight:56, maxWidth:200, objectFit:"contain", marginBottom:16 }} />
            : <div style={{ fontSize:24, fontWeight:800, color:"#fff", marginBottom:8 }}>{name}</div>
          }
          <div style={{ fontSize:14, color:"rgba(255,255,255,0.5)" }}>Sign in to your support portal</div>
        </div>

        <div style={{ background:"rgba(255,255,255,0.05)", borderRadius:16, padding:32, border:"1px solid rgba(255,255,255,0.08)" }}>
          {expectedEmail && (
            <div style={{ background:"rgba(29,78,216,0.12)", border:"1px solid rgba(59,130,246,0.35)", borderRadius:10, padding:"12px 14px", marginBottom:16, fontSize:12.5, color:"#bfdbfe", lineHeight:1.55, display:"flex", gap:9 }}>
              <Lock size={15} style={{ flexShrink:0, marginTop:1 }} />
              <span>This secure link is locked to <strong style={{ color:"#fff" }}>{expectedEmail}</strong>. Sign in with that account to view it.</span>
            </div>
          )}
          {error && (
            <div style={{ background:"#7f1d1d", color:"#fca5a5", padding:"10px 14px", borderRadius:8, fontSize:13, marginBottom:16, display:"flex", alignItems:"center", gap:8 }}>
              <AlertCircle size={14} /> {error}
            </div>
          )}

          {/* Microsoft SSO button — shown when client has Azure tenant linked */}
          {ssoEnabled && (
            <>
              <button onClick={signInWithMicrosoft} style={{
                width:"100%", padding:"11px 16px", background:"#fff", border:"none",
                borderRadius:8, color:"#1a1a1a", fontSize:14, fontWeight:600,
                cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:10, marginBottom:16,
              }}>
                <svg width="18" height="18" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
                  <rect x="1"  y="1"  width="9" height="9" fill="#f25022"/>
                  <rect x="11" y="1"  width="9" height="9" fill="#7fba00"/>
                  <rect x="1"  y="11" width="9" height="9" fill="#00a4ef"/>
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                </svg>
                Sign in with Microsoft
              </button>
              <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16 }}>
                <div style={{ flex:1, height:1, background:"rgba(255,255,255,0.1)" }} />
                <span style={{ fontSize:11, color:"rgba(255,255,255,0.3)" }}>or use password</span>
                <div style={{ flex:1, height:1, background:"rgba(255,255,255,0.1)" }} />
              </div>
            </>
          )}

          <div style={{ marginBottom:16 }}>
            <label style={{ display:"block", fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.5)", marginBottom:6 }}>Email</label>
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === "Enter" && submit()}
              placeholder="you@company.com"
              style={{ width:"100%", background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:8, padding:"10px 14px", color:"#fff", fontSize:14, outline:"none", boxSizing:"border-box" }}
            />
          </div>

          <div style={{ marginBottom:24 }}>
            <label style={{ display:"block", fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.5)", marginBottom:6 }}>Password</label>
            <input
              type="password" value={password} onChange={e => setPass(e.target.value)}
              onKeyDown={e => e.key === "Enter" && submit()}
              placeholder="••••••••"
              style={{ width:"100%", background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:8, padding:"10px 14px", color:"#fff", fontSize:14, outline:"none", boxSizing:"border-box" }}
            />
          </div>

          <button onClick={submit} disabled={loading} style={{
            width:"100%", padding:"12px", background:primary, border:"none",
            borderRadius:8, color:"#fff", fontSize:14, fontWeight:700,
            cursor: loading ? "default" : "pointer", opacity: loading ? 0.7 : 1,
          }}>
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </div>

        <div style={{ textAlign:"center", marginTop:24, fontSize:12, color:"rgba(255,255,255,0.25)" }}>
          Powered by MIDTC Support Platform
        </div>
      </div>
    </div>
  );
}

// =============================================================
//  PORTAL LAYOUT  (nav + outlet)
// =============================================================
function PortalLayout({ branding, contact, onLogout, children }) {
  const isMobile = useIsMobile();
  // Nav starts open on desktop, hidden on phones (where it overlays as a drawer)
  const [mobileOpen, setMobileOpen] = useState(() => !window.matchMedia("(max-width: 768px)").matches);
  const { identifier } = useParams();
  const primary = branding.primary_color || "#4f6fff";
  const bg      = branding.accent_color  || "#0f172a";
  const logo    = branding.logo_url ? `${API_URL}${branding.logo_url}` : null;
  const name    = branding.company_name  || "Support Portal";
  const isAdmin = contact.portalRole === "admin";

  // Absolute paths — PortalLayout is rendered outside <Routes>, so relative
  // NavLinks would resolve against the current URL and double-append the
  // target segment on every click (e.g. .../dashboard/dashboard/dashboard).
  const base = `/clientportal/${identifier}`;
  const navItems = [
    { to: `${base}/dashboard`,        label:"Dashboard",  icon:LayoutDashboard },
    { to: `${base}/tickets`,          label:"My Tickets", icon:Ticket          },
    { to: `${base}/knowledge-base`,   label:"Knowledge",  icon:BookOpen        },
    ...(isAdmin ? [{ to:`${base}/devices`, label:"Devices", icon:Monitor }] : []),
  ];

  const NavItems = () => navItems.map(({ to, label, icon:Icon }) => (
    <NavLink key={to} to={to}
      style={({ isActive }) => ({
        display:"flex", alignItems:"center", gap:10, padding:"10px 16px",
        color: isActive ? "#fff" : "rgba(255,255,255,0.55)",
        background: isActive ? "rgba(255,255,255,0.12)" : "none",
        borderRadius:8, fontSize:14, fontWeight: isActive ? 600 : 400,
        textDecoration:"none", transition:"all 0.15s",
      })}
      onClick={() => { if (isMobile) setMobileOpen(false); }}
    >
      <Icon size={17} /> {label}
    </NavLink>
  ));

  return (
    <div style={{ minHeight:"100vh", background:bg, display:"flex", flexDirection:"column" }}>
      {/* Top nav */}
      <header style={{ background:primary, padding:"0 24px", height:56, display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0, position:"sticky", top:0, zIndex:50 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <button onClick={() => setMobileOpen(!mobileOpen)}
            style={{ background:"none", border:"none", color:"rgba(255,255,255,0.7)", cursor:"pointer", display:"flex", padding:4 }}>
            <Menu size={20} />
          </button>
          {logo
            ? <img src={logo} alt={name} style={{ height:32, maxWidth:140, objectFit:"contain" }} />
            : <span style={{ fontWeight:700, fontSize:16, color:"#fff" }}>{name}</span>
          }
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <span style={{ fontSize:13, color:"rgba(255,255,255,0.7)" }}>
            {contact.firstName} {contact.lastName}
          </span>
          <button onClick={onLogout} style={{ background:"rgba(255,255,255,0.15)", border:"none", borderRadius:6, padding:"6px 10px", color:"#fff", cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontSize:12 }}>
            <LogOut size={13} /> Sign Out
          </button>
        </div>
      </header>

      <div style={{ display:"flex", flex:1 }}>
        {/* Backdrop — phone drawer only */}
        {isMobile && mobileOpen && (
          <div onClick={() => setMobileOpen(false)}
            style={{ position:"fixed", top:56, left:0, right:0, bottom:0, background:"rgba(2,6,23,0.6)", zIndex:55 }} />
        )}

        {/* Sidebar nav — inline on desktop, slide-over drawer on phones */}
        <aside style={{
          ...(isMobile ? {
            position:"fixed", top:56, bottom:0, left:0, zIndex:60,
            width:240,
            transform: mobileOpen ? "translateX(0)" : "translateX(-100%)",
            transition:"transform 0.22s ease",
            background: bg,
            boxShadow: mobileOpen ? "8px 0 40px rgba(0,0,0,0.5)" : "none",
            borderRight:"1px solid rgba(255,255,255,0.08)",
            padding:"12px 8px",
          } : {
            width: mobileOpen ? 220 : 0,
            background:"rgba(0,0,0,0.3)",
            overflow:"hidden",
            transition:"width 0.2s ease",
            flexShrink:0,
            padding: mobileOpen ? "12px 8px" : 0,
          }),
          display:"flex", flexDirection:"column", gap:2,
        }}>
          <NavItems />
        </aside>

        {/* Page content */}
        <main style={{ flex:1, minWidth:0, padding: isMobile ? "16px 14px" : "24px 28px", maxWidth:1100, width:"100%" }}>
          {children}
        </main>
      </div>
    </div>
  );
}

// =============================================================
//  PORTAL DASHBOARD
// =============================================================
function PortalDashboard({ branding, contact }) {
  const isMobile = useIsMobile();
  const [tickets, setTickets] = useState([]);
  const [devices, setDevices] = useState([]);
  const navigate = useNavigate();
  const { identifier } = useParams();
  const base = `/clientportal/${identifier}`;
  const primary  = branding.primary_color || "#4f6fff";

  useEffect(() => {
    getPortalTickets().then(r => setTickets(r.data)).catch(() => {});
    if (contact.portalRole === "admin") getPortalDevices().then(r => setDevices(r.data)).catch(() => {});
  }, [contact.portalRole]);

  const open     = tickets.filter(t => !["Resolved","Closed"].includes(t.status));
  const breaches = tickets.filter(t => t.sla_status === "breach");
  const recent   = tickets.slice(0, 5);

  const StatCard = ({ label, value, color }) => (
    <div style={{ background:"rgba(255,255,255,0.06)", borderRadius:12, padding:"16px 20px", border:"1px solid rgba(255,255,255,0.08)" }}>
      <div style={{ fontSize:28, fontWeight:800, color: color || "#fff" }}>{value}</div>
      <div style={{ fontSize:13, color:"rgba(255,255,255,0.5)", marginTop:4 }}>{label}</div>
    </div>
  );

  return (
    <div>
      {/* Welcome */}
      <div style={{ marginBottom:28 }}>
        <h2 style={{ margin:"0 0 4px", fontSize:22, fontWeight:700, color:"#fff" }}>
          Welcome back, {contact.firstName}
        </h2>
        <p style={{ margin:0, fontSize:14, color:"rgba(255,255,255,0.45)" }}>
          {branding.welcome_msg || "Here's a summary of your support activity."}
        </p>
      </div>

      {/* Stats */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:14, marginBottom:28 }}>
        <StatCard label="Open Tickets"    value={open.length}     color="#93c5fd" />
        <StatCard label="Total Tickets"   value={tickets.length}  />
        <StatCard label="SLA Breaches"    value={breaches.length} color={breaches.length > 0 ? "#fca5a5" : "#86efac"} />
        {contact.portalRole === "admin" && (
          <StatCard label="Devices"       value={devices.length}  color="#c4b5fd" />
        )}
      </div>

      {/* Quick actions */}
      <div style={{ display:"flex", gap:12, marginBottom:28 }}>
        <button onClick={() => navigate(`${base}/tickets/new`)} style={{
          display:"flex", alignItems:"center", gap:8, padding:"10px 20px",
          background:primary, border:"none", borderRadius:8, color:"#fff",
          fontSize:13, fontWeight:600, cursor:"pointer",
        }}>
          <Plus size={15} /> New Ticket
        </button>
        <button onClick={() => navigate(`${base}/tickets`)} style={{
          display:"flex", alignItems:"center", gap:8, padding:"10px 20px",
          background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.12)",
          borderRadius:8, color:"rgba(255,255,255,0.7)", fontSize:13, cursor:"pointer",
        }}>
          View All Tickets
        </button>
      </div>

      {/* Recent tickets */}
      {recent.length > 0 && (
        <div style={{ background:"rgba(255,255,255,0.04)", borderRadius:12, border:"1px solid rgba(255,255,255,0.08)", overflow:"hidden" }}>
          <div style={{ padding:"14px 20px", borderBottom:"1px solid rgba(255,255,255,0.08)", fontSize:13, fontWeight:600, color:"rgba(255,255,255,0.7)" }}>
            Recent Tickets
          </div>
          {recent.map(t => {
            const sla = SLA_COLORS[t.sla_status] || SLA_COLORS.ok;
            const st  = STATUS_COLORS[t.status]  || STATUS_COLORS.Open;
            return (
              <div key={t.id} onClick={() => navigate(`${base}/tickets/${t.id}`)}
                style={{ padding:"12px 20px", borderBottom:"1px solid rgba(255,255,255,0.05)", display:"flex", alignItems:"center", gap:12, cursor:"pointer", transition:"background 0.1s" }}
                onMouseEnter={e => e.currentTarget.style.background="rgba(255,255,255,0.04)"}
                onMouseLeave={e => e.currentTarget.style.background="none"}
              >
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:500, color:"#fff", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{t.subject}</div>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", marginTop:2 }}>{t.ticket_number} · {timeAgo(t.last_activity_at)}</div>
                  {isMobile && (
                    <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:6 }}>
                      <Badge label={t.status}  bg={st.bg}  color={st.color} />
                      <Badge label={sla.label} bg={sla.bg} color={sla.color} />
                    </div>
                  )}
                </div>
                {!isMobile && <Badge label={t.status}     bg={st.bg}   color={st.color} />}
                {!isMobile && <Badge label={sla.label}    bg={sla.bg}  color={sla.color} />}
                <ChevronRight size={14} color="rgba(255,255,255,0.25)" />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// =============================================================
//  PORTAL TICKET LIST
// =============================================================
function PortalTicketList({ branding }) {
  const isMobile = useIsMobile();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { identifier } = useParams();
  const base = `/clientportal/${identifier}`;
  const primary  = branding.primary_color || "#4f6fff";

  useEffect(() => {
    getPortalTickets().then(r => { setTickets(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24 }}>
        <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:"#fff" }}>My Tickets</h2>
        <button onClick={() => navigate(`${base}/tickets/new`)} style={{
          display:"flex", alignItems:"center", gap:8, padding:"9px 18px",
          background:primary, border:"none", borderRadius:8,
          color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer",
        }}>
          <Plus size={14} /> New Ticket
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign:"center", padding:48, color:"rgba(255,255,255,0.3)" }}>Loading tickets...</div>
      ) : tickets.length === 0 ? (
        <div style={{ textAlign:"center", padding:48, color:"rgba(255,255,255,0.3)" }}>
          <Ticket size={40} style={{ marginBottom:12, opacity:0.3 }} />
          <div>No tickets yet</div>
          <button onClick={() => navigate(`${base}/tickets/new`)} style={{ marginTop:16, padding:"9px 20px", background:primary, border:"none", borderRadius:8, color:"#fff", fontSize:13, cursor:"pointer" }}>
            Submit your first ticket
          </button>
        </div>
      ) : (
        <div style={{ background:"rgba(255,255,255,0.04)", borderRadius:12, border:"1px solid rgba(255,255,255,0.08)", overflow:"hidden" }}>
          {/* Table header — desktop only */}
          {!isMobile && (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 120px 100px 100px 120px 32px", gap:12, padding:"10px 20px", borderBottom:"1px solid rgba(255,255,255,0.08)", fontSize:11, fontWeight:600, color:"rgba(255,255,255,0.35)", textTransform:"uppercase", letterSpacing:"0.06em" }}>
              <span>Subject</span><span>Status</span><span>Priority</span><span>SLA</span><span>Last Update</span><span />
            </div>
          )}
          {tickets.map(t => {
            const sla = SLA_COLORS[t.sla_status] || SLA_COLORS.ok;
            const st  = STATUS_COLORS[t.status]  || STATUS_COLORS.Open;
            if (isMobile) return (
              <div key={t.id} onClick={() => navigate(`${base}/tickets/${t.id}`)}
                style={{ padding:"14px 16px", borderBottom:"1px solid rgba(255,255,255,0.05)", cursor:"pointer" }}
              >
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:14, fontWeight:500, color:"#fff", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{t.subject}</div>
                    <div style={{ fontSize:11, color:"rgba(255,255,255,0.3)", marginTop:2 }}>{t.ticket_number} · {timeAgo(t.last_activity_at)}</div>
                  </div>
                  <ChevronRight size={16} color="rgba(255,255,255,0.25)" style={{ flexShrink:0 }} />
                </div>
                <div style={{ display:"flex", flexWrap:"wrap", alignItems:"center", gap:6, marginTop:8 }}>
                  <Badge label={t.status} bg={st.bg} color={st.color} />
                  <Badge label={sla.label} bg={sla.bg} color={sla.color} />
                  <span style={{ display:"inline-flex", alignItems:"center", gap:5 }}>
                    <span style={{ width:8, height:8, borderRadius:"50%", background:PRIORITY_COLORS[t.priority]||"#94a3b8", display:"inline-block" }} />
                    <span style={{ fontSize:11, color:"rgba(255,255,255,0.6)" }}>{t.priority}</span>
                  </span>
                </div>
              </div>
            );
            return (
              <div key={t.id} onClick={() => navigate(`${base}/tickets/${t.id}`)}
                style={{ display:"grid", gridTemplateColumns:"1fr 120px 100px 100px 120px 32px", gap:12, padding:"13px 20px", borderBottom:"1px solid rgba(255,255,255,0.05)", alignItems:"center", cursor:"pointer", transition:"background 0.1s" }}
                onMouseEnter={e => e.currentTarget.style.background="rgba(255,255,255,0.04)"}
                onMouseLeave={e => e.currentTarget.style.background="none"}
              >
                <div style={{ minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:500, color:"#fff", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{t.subject}</div>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.3)", marginTop:2 }}>{t.ticket_number} · {fmtDate(t.created_at)}</div>
                </div>
                <Badge label={t.status}   bg={st.bg}  color={st.color}  />
                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <div style={{ width:8, height:8, borderRadius:"50%", background:PRIORITY_COLORS[t.priority]||"#94a3b8", flexShrink:0 }} />
                  <span style={{ fontSize:12, color:"rgba(255,255,255,0.6)" }}>{t.priority}</span>
                </div>
                <Badge label={sla.label} bg={sla.bg} color={sla.color} />
                <span style={{ fontSize:12, color:"rgba(255,255,255,0.4)" }}>{timeAgo(t.last_activity_at)}</span>
                <ChevronRight size={14} color="rgba(255,255,255,0.25)" />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// =============================================================
//  PORTAL TICKET DETAIL
// =============================================================
function PortalTicketDetail({ branding }) {
  const { ticketId } = useParams();
  const navigate     = useNavigate();
  const [ticket,  setTicket]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply,   setReply]   = useState("");
  const [sending, setSending] = useState(false);
  const primary = branding.primary_color || "#4f6fff";

  // `silent` refreshes the thread in the background (polling) without flashing the
  // loading spinner — so new agent/FTR replies appear on the customer's screen
  // automatically, no manual refresh needed.
  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    return getPortalTicket(ticketId)
      .then(r => { setTicket(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [ticketId]);

  useEffect(() => { load(); }, [load]);

  // Auto-update: poll the ticket every 15s while the tab is visible, and refresh
  // immediately when the user returns to the tab. Keeps the conversation live.
  useEffect(() => {
    const tick = () => { if (document.visibilityState === "visible") load(true); };
    const id = setInterval(tick, 15000);
    document.addEventListener("visibilitychange", tick);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", tick); };
  }, [load]);

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await addPortalResponse(ticketId, { body: reply });
      setReply("");
      load();
    } catch {}
    setSending(false);
  };

  if (loading) return <div style={{ padding:48, textAlign:"center", color:"rgba(255,255,255,0.3)" }}>Loading ticket...</div>;
  if (!ticket) return <div style={{ padding:48, textAlign:"center", color:"#fca5a5" }}>Ticket not found</div>;

  const sla = SLA_COLORS[ticket.sla_status] || SLA_COLORS.ok;
  const st  = STATUS_COLORS[ticket.status]  || STATUS_COLORS.Open;

  return (
    <div>
      {/* Back button */}
      <button onClick={() => navigate(-1)} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.4)", cursor:"pointer", fontSize:13, marginBottom:16, padding:0, display:"flex", alignItems:"center", gap:6 }}>
        ← Back to Tickets
      </button>

      {/* Ticket header */}
      <div style={{ background:"rgba(255,255,255,0.05)", borderRadius:12, padding:"20px 24px", marginBottom:20, border:"1px solid rgba(255,255,255,0.08)" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:16, flexWrap:"wrap" }}>
          <div>
            <div style={{ fontSize:12, color:"rgba(255,255,255,0.35)", marginBottom:4 }}>{ticket.ticket_number}</div>
            <h2 style={{ margin:"0 0 12px", fontSize:18, fontWeight:700, color:"#fff" }}>{ticket.subject}</h2>
            <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
              <Badge label={ticket.status}   bg={st.bg}   color={st.color} />
              <Badge label={sla.label}       bg={sla.bg}  color={sla.color} />
              <Badge label={ticket.priority} bg="rgba(255,255,255,0.08)" color={PRIORITY_COLORS[ticket.priority]||"#94a3b8"} />
              <Badge label={ticket.category} bg="rgba(255,255,255,0.06)" color="rgba(255,255,255,0.5)" />
            </div>
            {Array.isArray(ticket.cc_emails) && ticket.cc_emails.length > 0 && (
              <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:10, alignItems:"center" }}>
                <span style={{ fontSize:11, color:"rgba(255,255,255,0.3)", fontWeight:600, marginRight:2 }}>CC:</span>
                {ticket.cc_emails.map(e => (
                  <span key={e} style={{ fontSize:11, color:"rgba(255,255,255,0.55)", background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:99, padding:"2px 10px" }}>
                    {e}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div style={{ fontSize:12, color:"rgba(255,255,255,0.35)", textAlign:"right" }}>
            <div>Submitted {fmtDate(ticket.created_at)}</div>
            <div style={{ marginTop:4 }}>Last update {timeAgo(ticket.last_activity_at)}</div>
            {ticket.assigned_to_name && <div style={{ marginTop:4 }}>Agent: {ticket.assigned_to_name}</div>}
            {ticket.sla_due_at && <div style={{ marginTop:4 }}>SLA Due: {fmtTime(ticket.sla_due_at)}</div>}
          </div>
        </div>

        {/* Original description */}
        <div style={{ marginTop:16, padding:"14px 16px", background:"rgba(0,0,0,0.2)", borderRadius:8, fontSize:13, color:"rgba(255,255,255,0.65)", lineHeight:1.6, borderLeft:`3px solid ${primary}` }}>
          <MessageBody text={ticket.description} />
          {ticket.attachments?.length > 0 && (
            <div style={{ marginTop:10, paddingTop:10, borderTop:"1px solid rgba(255,255,255,0.08)", display:"flex", flexWrap:"wrap", gap:6 }}>
              {ticket.attachments.map((a, ai) => (
                <a key={ai} href={`${API_URL}${a.url}`} target="_blank" rel="noreferrer"
                  style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"4px 10px",
                    background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.15)",
                    borderRadius:6, fontSize:11, color:"rgba(255,255,255,0.7)", textDecoration:"none" }}>
                  <Paperclip size={10}/>{a.name}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Conversation */}
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.35)", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:12 }}>
          Conversation ({ticket.responses?.length || 0} replies)
        </div>
        {ticket.responses?.length === 0 && (
          <div style={{ padding:"20px", textAlign:"center", color:"rgba(255,255,255,0.25)", fontSize:13 }}>No replies yet — we'll respond shortly.</div>
        )}
        {ticket.responses?.map(r => {
          const isAgent = !!r.agent_name;
          return (
            <div key={r.id} style={{
              marginBottom:12, padding:"14px 18px",
              background: isAgent ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.2)",
              borderRadius:10, borderLeft:`3px solid ${isAgent ? primary : "rgba(255,255,255,0.15)"}`,
            }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                <span style={{ fontSize:12, fontWeight:600, color: isAgent ? "#93c5fd" : "rgba(255,255,255,0.5)" }}>
                  {isAgent ? `${r.agent_name} (Support)` : "You"}
                </span>
                <span style={{ fontSize:11, color:"rgba(255,255,255,0.25)" }}>{fmtTime(r.created_at)}</span>
              </div>
              <div style={{ fontSize:13, color:"rgba(255,255,255,0.75)", lineHeight:1.6 }}><MessageBody text={r.body} /></div>
              {r.attachments?.length > 0 && (
                <div style={{ marginTop:10, paddingTop:10, borderTop:"1px solid rgba(255,255,255,0.08)", display:"flex", flexWrap:"wrap", gap:6 }}>
                  {r.attachments.map((a, ai) => (
                    <a key={ai} href={`${API_URL}${a.url}`} target="_blank" rel="noreferrer"
                      style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"4px 10px",
                        background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.15)",
                        borderRadius:6, fontSize:11, color:"rgba(255,255,255,0.7)", textDecoration:"none" }}>
                      <Paperclip size={10}/>{a.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Reply box — hide if resolved/closed */}
      {!["Resolved","Closed"].includes(ticket.status) && (
        <div style={{ background:"rgba(255,255,255,0.04)", borderRadius:12, padding:20, border:"1px solid rgba(255,255,255,0.08)" }}>
          <div style={{ fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.4)", marginBottom:10 }}>Add a Reply</div>
          <textarea
            value={reply}
            onChange={e => setReply(e.target.value)}
            placeholder="Type your reply here..."
            rows={4}
            style={{ width:"100%", background:"rgba(0,0,0,0.3)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"12px 14px", color:"#fff", fontSize:13, resize:"vertical", outline:"none", boxSizing:"border-box" }}
          />
          <div style={{ display:"flex", justifyContent:"flex-end", marginTop:10 }}>
            <button onClick={sendReply} disabled={sending || !reply.trim()} style={{
              display:"flex", alignItems:"center", gap:8, padding:"9px 20px",
              background: reply.trim() ? primary : "rgba(255,255,255,0.08)",
              border:"none", borderRadius:8, color: reply.trim() ? "#fff" : "rgba(255,255,255,0.3)",
              fontSize:13, fontWeight:600, cursor: reply.trim() ? "pointer" : "default",
            }}>
              <Send size={13} /> {sending ? "Sending..." : "Send Reply"}
            </button>
          </div>
        </div>
      )}
      {["Resolved","Closed"].includes(ticket.status) && (
        <div style={{ display:"flex", alignItems:"center", gap:10, padding:"14px 18px", background:"#14532d", borderRadius:10, color:"#86efac", fontSize:13 }}>
          <CheckCircle size={16} /> This ticket has been {ticket.status.toLowerCase()}. Reply to reopen it.
        </div>
      )}
    </div>
  );
}

// =============================================================
//  PORTAL NEW TICKET FORM
// =============================================================
function PortalNewTicket({ branding }) {
  const navigate  = useNavigate();
  const { identifier } = useParams();
  const base = `/clientportal/${identifier}`;
  const primary   = branding.primary_color || "#4f6fff";
  const [form, setForm] = useState({ subject:"", description:"", category:"General", priority:"Medium", cc_emails:[] });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [ccInput, setCcInput] = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addCc = () => {
    const email = ccInput.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    if (!form.cc_emails.includes(email)) set("cc_emails", [...form.cc_emails, email]);
    setCcInput("");
  };

  const removeCc = (email) => set("cc_emails", form.cc_emails.filter(e => e !== email));

  const submit = async () => {
    if (!form.subject || !form.description) { setError("Subject and description are required"); return; }
    setSubmitting(true); setError("");
    try {
      const res = await createPortalTicket(form);
      navigate(`${base}/tickets/${res.data.id}`);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to submit ticket");
    }
    setSubmitting(false);
  };

  const selectStyle = { width:"100%", background:"rgba(0,0,0,0.3)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#fff", fontSize:13, outline:"none" };
  const labelStyle  = { display:"block", fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.45)", marginBottom:6 };

  return (
    <div style={{ maxWidth:640 }}>
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:24 }}>
        <button onClick={() => navigate(-1)} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.4)", cursor:"pointer", padding:0, fontSize:13 }}>←</button>
        <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:"#fff" }}>Submit a Ticket</h2>
      </div>

      {error && (
        <div style={{ background:"#7f1d1d", color:"#fca5a5", padding:"10px 14px", borderRadius:8, fontSize:13, marginBottom:20, display:"flex", alignItems:"center", gap:8 }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}

      <div style={{ background:"rgba(255,255,255,0.04)", borderRadius:14, padding:28, border:"1px solid rgba(255,255,255,0.08)", display:"flex", flexDirection:"column", gap:18 }}>
        <div>
          <label style={labelStyle}>Subject *</label>
          <input value={form.subject} onChange={e => set("subject", e.target.value)} placeholder="Brief description of the issue"
            style={{ ...selectStyle, padding:"10px 14px" }} />
        </div>
        <div>
          <label style={labelStyle}>Description *</label>
          <textarea value={form.description} onChange={e => set("description", e.target.value)} placeholder="Please describe the issue in detail — what happened, when, and any steps to reproduce." rows={5}
            style={{ ...selectStyle, resize:"vertical", lineHeight:1.6 }} />
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
          <div>
            <label style={labelStyle}>Category</label>
            <select value={form.category} onChange={e => set("category", e.target.value)} style={selectStyle}>
              {["General","Hardware","Software","Network","Email","Security","Other"].map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Priority</label>
            <select value={form.priority} onChange={e => set("priority", e.target.value)} style={selectStyle}>
              {["Low","Medium","High","Critical"].map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label style={labelStyle}>CC (optional)</label>
          <div style={{ display:"flex", gap:8 }}>
            <input
              value={ccInput}
              onChange={e => setCcInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addCc(); } }}
              placeholder="Add email address and press Enter"
              style={{ ...selectStyle, flex:1, padding:"9px 14px" }}
            />
            <button type="button" onClick={addCc} style={{ padding:"9px 16px", background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:8, color:"rgba(255,255,255,0.6)", fontSize:13, cursor:"pointer", whiteSpace:"nowrap" }}>
              Add
            </button>
          </div>
          {form.cc_emails.length > 0 && (
            <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:8 }}>
              {form.cc_emails.map(e => (
                <span key={e} style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:12, color:"rgba(255,255,255,0.65)", background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:99, padding:"3px 10px" }}>
                  {e}
                  <button type="button" onClick={() => removeCc(e)} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.35)", cursor:"pointer", padding:0, lineHeight:1, fontSize:14 }}>×</button>
                </span>
              ))}
            </div>
          )}
        </div>
        <div style={{ display:"flex", justifyContent:"flex-end", gap:10, paddingTop:4 }}>
          <button onClick={() => navigate(-1)} style={{ padding:"9px 20px", background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, color:"rgba(255,255,255,0.5)", fontSize:13, cursor:"pointer" }}>
            Cancel
          </button>
          <button onClick={submit} disabled={submitting} style={{ display:"flex", alignItems:"center", gap:8, padding:"9px 24px", background:primary, border:"none", borderRadius:8, color:"#fff", fontSize:13, fontWeight:600, cursor: submitting ? "default" : "pointer", opacity: submitting ? 0.7 : 1 }}>
            <Send size={13} /> {submitting ? "Submitting..." : "Submit Ticket"}
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================
//  PORTAL DEVICES  (client_admin only)
// =============================================================
function PortalDevices() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPortalDevices().then(r => { setDevices(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const scoreColor = (s) => s >= 80 ? "#22c55e" : s >= 50 ? "#eab308" : "#ef4444";

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24 }}>
        <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:"#fff" }}>Devices</h2>
        <button onClick={() => { setLoading(true); getPortalDevices().then(r => { setDevices(r.data); setLoading(false); }); }} style={{ background:"none", border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"7px 14px", color:"rgba(255,255,255,0.5)", cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontSize:12 }}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign:"center", padding:48, color:"rgba(255,255,255,0.3)" }}>Loading devices...</div>
      ) : devices.length === 0 ? (
        <div style={{ textAlign:"center", padding:48, color:"rgba(255,255,255,0.3)" }}>
          <Monitor size={40} style={{ marginBottom:12, opacity:0.3 }} />
          <div>No devices found</div>
        </div>
      ) : (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))", gap:16 }}>
          {devices.map(d => {
            const score = d.health_score ?? 100;
            const alerts = typeof d.alerts === "string" ? JSON.parse(d.alerts || "[]") : (d.alerts || []);
            return (
              <div key={d.id} style={{ background:"rgba(255,255,255,0.05)", borderRadius:12, padding:20, border:"1px solid rgba(255,255,255,0.08)" }}>
                {/* Device name + score */}
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                  <div>
                    <div style={{ fontSize:14, fontWeight:600, color:"#fff" }}>{d.device_name}</div>
                    <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", marginTop:2 }}>{d.os} {d.os_version}</div>
                  </div>
                  <div style={{ textAlign:"center" }}>
                    <div style={{ fontSize:22, fontWeight:800, color:scoreColor(score) }}>{score}</div>
                    <div style={{ fontSize:10, color:"rgba(255,255,255,0.3)" }}>Health</div>
                  </div>
                </div>

                {/* Metrics */}
                {[
                  { label:"CPU",  val:d.cpu_load_percent },
                  { label:"RAM",  val:d.ram_used_percent },
                  { label:"Disk", val:d.disk_used_percent },
                ].map(({ label, val }) => val != null && (
                  <div key={label} style={{ marginBottom:8 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:"rgba(255,255,255,0.4)", marginBottom:3 }}>
                      <span>{label}</span><span>{Math.round(val)}%</span>
                    </div>
                    <div style={{ height:5, background:"rgba(255,255,255,0.08)", borderRadius:3, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${val}%`, background: val>=90?"#ef4444":val>=75?"#eab308":"#22c55e", borderRadius:3 }} />
                    </div>
                  </div>
                ))}

                {/* Alerts */}
                {alerts.length > 0 && (
                  <div style={{ marginTop:10, display:"flex", flexDirection:"column", gap:4 }}>
                    {alerts.map((a, i) => (
                      <div key={i} style={{ fontSize:11, color:"#fcd34d", background:"rgba(253,186,116,0.08)", padding:"4px 8px", borderRadius:4, display:"flex", alignItems:"center", gap:6 }}>
                        <AlertCircle size={10} /> {a}
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ marginTop:10, fontSize:11, color:"rgba(255,255,255,0.2)" }}>
                  Last seen: {timeAgo(d.checked_at || d.last_seen)}
                  {d.logged_in_user && ` · ${d.logged_in_user}`}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// =============================================================
// =============================================================
//  PORTAL — SECURE LINK VIEWER
// =============================================================
function SecureLinkPortalView({ branding, contact, onLogout }) {
  const { shareToken } = useParams();
  const primary = branding?.primary_color || "#1d4ed8";
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [denied,  setDenied]  = useState(null); // { recipient_email, your_email } on wrong-account

  useEffect(() => {
    getPortalSecureLink(shareToken)
      .then(r => setData(r.data))
      .catch(e => {
        const d = e?.response?.data;
        if (d?.code === "wrong_account") setDenied({ recipient_email: d.recipient_email, your_email: d.your_email || contact?.email });
        else setError(d?.error || "Unable to load secure link.");
      })
      .finally(() => setLoading(false));
  }, [shareToken]);

  // Switch account: drop the portal session, then re-auth as the intended recipient.
  // SSO clients go straight to the Microsoft chooser (login_hint + select_account);
  // password clients land on the prefilled login form at this same secure URL.
  const switchAccount = () => {
    if (onLogout) onLogout();
    if (branding?.sso_enabled) {
      const hint = denied?.recipient_email ? `&login_hint=${encodeURIComponent(denied.recipient_email)}` : "";
      window.location.href = `${API_URL}/api/auth/m365/portal/login?return=${encodeURIComponent(window.location.pathname)}${hint}`;
    }
    // else: onLogout() re-renders PortalLogin (prefilled with the recipient) at this URL
  };

  if (loading) return (
    <div style={{ padding:48, textAlign:"center", color:"#64748b", fontSize:14 }}>
      <div style={{ width:32, height:32, border:`3px solid ${primary}`, borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.7s linear infinite", margin:"0 auto 12px" }} />
      Loading secure content…
    </div>
  );

  if (denied) return (
    <div style={{ padding:32, maxWidth:520 }}>
      <div style={{ background:"#1e293b", border:"1px solid #7f1d1d", borderRadius:12, padding:28, textAlign:"center" }}>
        <div style={{ fontSize:36, marginBottom:12 }}>🔒</div>
        <div style={{ fontSize:16, fontWeight:700, color:"#f1f5f9", marginBottom:8 }}>Wrong account</div>
        <div style={{ fontSize:14, color:"#94a3b8", lineHeight:1.6 }}>
          This secure link was sent to <strong style={{ color:"#e2e8f0" }}>{denied.recipient_email}</strong>.<br/>
          You're signed in as <strong style={{ color:"#e2e8f0" }}>{denied.your_email}</strong>.
        </div>
        <button onClick={switchAccount} style={{ marginTop:20, width:"100%", padding:"12px", background:primary, border:"none", borderRadius:9, color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer" }}>
          Sign in as {denied.recipient_email}
        </button>
        <div style={{ marginTop:14, fontSize:12, color:"#64748b", lineHeight:1.5 }}>
          If you can't sign in as {denied.recipient_email}, you don't have access to this content. Contact MIDTC Support if you believe this is an error.
        </div>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ padding:32, maxWidth:520 }}>
      <div style={{ background:"#1e293b", border:"1px solid #7f1d1d", borderRadius:12, padding:28, textAlign:"center" }}>
        <div style={{ fontSize:36, marginBottom:12 }}>🔒</div>
        <div style={{ fontSize:16, fontWeight:700, color:"#f1f5f9", marginBottom:8 }}>Access Denied</div>
        <div style={{ fontSize:14, color:"#94a3b8", lineHeight:1.6 }}>{error}</div>
      </div>
    </div>
  );

  if (!data) return null;

  const files = data.files || [];
  const baseUrl = process.env.REACT_APP_API_URL || "";

  return (
    <div style={{ padding:24, maxWidth:680 }}>
      {/* Header card */}
      <div style={{ background:"#1e293b", border:`1px solid rgba(59,130,246,0.25)`, borderRadius:12, overflow:"hidden", marginBottom:16 }}>
        <div style={{ background:`linear-gradient(135deg, ${primary}22, ${primary}0a)`, borderBottom:"1px solid rgba(59,130,246,0.15)", padding:"16px 20px", display:"flex", alignItems:"center", gap:10 }}>
          <Lock size={18} color={primary} />
          <div>
            <div style={{ fontSize:16, fontWeight:700, color:"#f1f5f9" }}>{data.title || "Secure Content"}</div>
            <div style={{ fontSize:11, color:"#64748b", marginTop:2 }}>
              Sent securely to <strong style={{ color:"#94a3b8" }}>{contact?.email}</strong>
              {data.remaining !== null && ` · ${data.remaining} view${data.remaining !== 1 ? "s" : ""} remaining`}
            </div>
          </div>
        </div>

        {/* Message */}
        {data.message && (
          <div style={{ padding:"16px 20px", borderBottom: files.length ? "1px solid #1e293b" : "none" }}>
            <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:".06em", color:"#475569", marginBottom:8 }}>Message</div>
            <div style={{ fontSize:14, color:"#cbd5e1", lineHeight:1.7, whiteSpace:"pre-wrap" }}>{data.message}</div>
          </div>
        )}

        {/* Files */}
        {files.length > 0 && (
          <div style={{ padding:"16px 20px" }}>
            <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:".06em", color:"#475569", marginBottom:10 }}>
              Attachments ({files.length})
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {files.map((f, i) => {
                const isImg = f.mimetype?.startsWith("image/");
                const isPdf = f.mimetype === "application/pdf";
                const icon  = isImg ? "🖼️" : isPdf ? "📄" : "📁";
                const size  = f.size > 1048576 ? `${(f.size/1048576).toFixed(1)} MB` : f.size > 1024 ? `${(f.size/1024).toFixed(0)} KB` : `${f.size} B`;
                return (
                  <a key={i} href={`${baseUrl}${f.url}`} download={f.name} target="_blank" rel="noreferrer"
                    style={{ display:"flex", alignItems:"center", gap:12, background:"#0f172a", border:"1px solid #334155", borderRadius:8, padding:"12px 14px", textDecoration:"none", color:"#f1f5f9", transition:"border-color .15s" }}
                    onMouseEnter={e => e.currentTarget.style.borderColor="#3b82f6"}
                    onMouseLeave={e => e.currentTarget.style.borderColor="#334155"}>
                    <span style={{ fontSize:20, flexShrink:0 }}>{icon}</span>
                    <span style={{ flex:1, fontSize:13, fontWeight:500, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{f.name}</span>
                    <span style={{ fontSize:11, color:"#64748b", flexShrink:0 }}>{size}</span>
                    <span style={{ background:primary, color:"#fff", fontSize:11, fontWeight:700, padding:"4px 10px", borderRadius:6, flexShrink:0 }}>↓ Download</span>
                  </a>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div style={{ fontSize:11, color:"#334155", textAlign:"center", lineHeight:1.6 }}>
        This secure content was shared with you by MIDTC Support. Do not forward this link to others.
      </div>
    </div>
  );
}

//  PORTAL APP  (root — handles slug, auth, routing)
// =============================================================
// =============================================================
//  PORTAL — KNOWLEDGE BASE
// =============================================================
function PortalKnowledgeBase({ branding }) {
  const primary = branding?.primary_color || "#4f6fff";
  const [articles,   setArticles]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [filterCat,  setFilterCat]  = useState("");
  const [selected,   setSelected]   = useState(null);
  const [artLoading, setArtLoading] = useState(false);

  useEffect(() => {
    Promise.all([getPortalKBCategories(), getPortalKBArticles()])
      .then(([catRes, artRes]) => { setCategories(catRes.data); setArticles(artRes.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = articles.filter(a => {
    if (filterCat && String(a.category_id) !== String(filterCat)) return false;
    if (search && !a.title.toLowerCase().includes(search.toLowerCase()) &&
        !(a.excerpt || "").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const handleSelect = async (id) => {
    setArtLoading(true);
    try { const r = await getPortalKBArticle(id); setSelected(r.data); }
    catch {}
    finally { setArtLoading(false); }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" }) : "";

  if (selected) {
    return (
      <div style={{ padding:24, maxWidth:860 }}>
        <button
          onClick={() => setSelected(null)}
          style={{ background:"none", border:"none", color:"#64748b", cursor:"pointer", display:"flex", alignItems:"center", gap:4, fontSize:13, marginBottom:16 }}
        >
          <ArrowLeft size={14} /> Back to Knowledge Base
        </button>
        <div style={{ background:"#0f172a", border:"1px solid #1e293b", borderRadius:10, padding:28 }}>
          <div style={{ marginBottom:10 }}>
            {selected.category_name && (
              <span style={{ background:selected.category_color||"#1e3a5f", color:"#93c5fd", fontSize:11, padding:"2px 8px", borderRadius:20, marginRight:8 }}>
                {selected.category_name}
              </span>
            )}
            {selected.client_id
              ? <span style={{ fontSize:11, color:"#64748b", display:"inline-flex", alignItems:"center", gap:3 }}><Lock size={9}/> Private</span>
              : <span style={{ fontSize:11, color:"#3b82f6", display:"inline-flex", alignItems:"center", gap:3 }}><Globe size={9}/> Public</span>
            }
          </div>
          <h1 style={{ fontSize:20, fontWeight:700, color:"#f1f5f9", marginBottom:6 }}>{selected.title}</h1>
          <p style={{ fontSize:12, color:"#475569", marginBottom:20 }}>Updated {fmtDate(selected.updated_at)} · {selected.view_count} view{selected.view_count !== 1 ? "s" : ""}</p>
          <div
            style={{ color:"#cbd5e1", lineHeight:1.8, fontSize:14 }}
            dangerouslySetInnerHTML={{ __html: selected.content }}
          />
          {selected.tags?.length > 0 && (
            <div style={{ marginTop:20, display:"flex", gap:6, flexWrap:"wrap" }}>
              {selected.tags.map(t => (
                <span key={t} style={{ background:"#1e293b", color:"#64748b", fontSize:11, padding:"2px 10px", borderRadius:20 }}>#{t}</span>
              ))}
            </div>
          )}
        </div>
        <style>{`
          .portal-kb h1{font-size:20px;font-weight:700;color:#f1f5f9;margin:20px 0 8px}
          .portal-kb h2{font-size:17px;font-weight:600;color:#e2e8f0;margin:16px 0 6px}
          .portal-kb p{margin:8px 0}
          .portal-kb ul,.portal-kb ol{padding-left:20px;margin:8px 0}
          .portal-kb pre{background:#1e293b;padding:12px;border-radius:6px;font-size:13px;color:#86efac}
          .portal-kb a{color:#60a5fa}
          .portal-kb img{max-width:100%;border-radius:6px}
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ padding:24, maxWidth:1000 }}>
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
        <BookOpen size={20} style={{ color:primary }} />
        <h1 style={{ fontSize:20, fontWeight:700, color:"#f1f5f9" }}>Knowledge Base</h1>
      </div>

      <div style={{ display:"flex", gap:10, marginBottom:20 }}>
        <div style={{ position:"relative", flex:1 }}>
          <Search size={14} style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", color:"#475569" }} />
          <input
            style={{ background:"#1e293b", border:"1px solid #334155", borderRadius:8, padding:"8px 12px 8px 32px", color:"#f1f5f9", fontSize:13, width:"100%", boxSizing:"border-box" }}
            placeholder="Search articles…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select
          style={{ background:"#1e293b", border:"1px solid #334155", borderRadius:8, padding:"8px 12px", color:"#f1f5f9", fontSize:13 }}
          value={filterCat}
          onChange={e => setFilterCat(e.target.value)}
        >
          <option value="">All Categories</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {loading ? (
        <div style={{ display:"flex", justifyContent:"center", padding:40 }}>
          <div style={{ width:24, height:24, border:`3px solid ${primary}`, borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.7s linear infinite" }} />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:"center", padding:48, color:"#475569" }}>
          <BookOpen size={36} style={{ marginBottom:10, opacity:0.3 }} />
          <p>No articles found</p>
        </div>
      ) : (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:12 }}>
          {filtered.map(a => (
            <div
              key={a.id}
              onClick={() => handleSelect(a.id)}
              style={{ background:"#0f172a", border:"1px solid #1e293b", borderRadius:10, padding:16, cursor:"pointer", transition:"border-color .15s" }}
              onMouseEnter={e => e.currentTarget.style.borderColor="#334155"}
              onMouseLeave={e => e.currentTarget.style.borderColor="#1e293b"}
            >
              {a.category_name && (
                <span style={{ background:a.category_color||"#1e3a5f", color:"#93c5fd", fontSize:10, padding:"2px 7px", borderRadius:20, display:"inline-block", marginBottom:8 }}>
                  {a.category_name}
                </span>
              )}
              <h3 style={{ fontSize:13, fontWeight:600, color:"#f1f5f9", marginBottom:6, lineHeight:1.4 }}>{a.title}</h3>
              {a.excerpt && (
                <p style={{ fontSize:12, color:"#64748b", lineHeight:1.5, display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden" }}>
                  {a.excerpt.replace(/<[^>]+>/g, "")}
                </p>
              )}
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:10, borderTop:"1px solid #1e293b", paddingTop:8 }}>
                <span style={{ fontSize:11, color:"#475569" }}>{fmtDate(a.updated_at)}</span>
                <span style={{ fontSize:11, color:"#475569", display:"flex", alignItems:"center", gap:3 }}><Eye size={10}/>{a.view_count}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================
//  DESKTOP APP DOWNLOAD NUDGE
//  Shown after login when the contact hasn't yet registered a device from the
//  desktop app. Signing into the desktop app auto-assigns their device to them
//  and their client (backend /api/devices/self-register), so this is what closes
//  the "unassigned device" gap. Dismissible per session; returns next login until
//  they've actually registered.
// =============================================================
function detectOS() {
  const ua = `${navigator.userAgent} ${navigator.platform}`.toLowerCase();
  if (/mac|iphone|ipad|ipod/.test(ua)) return "mac";
  if (/win/.test(ua)) return "windows";
  return "other";
}

function DesktopDownloadModal({ branding }) {
  const [show, setShow] = useState(false);
  const primary = branding?.primary_color || "#4f6fff";
  const os = detectOS();
  // Phones/tablets can't run the desktop installer — skip the nudge entirely
  // (it would also mis-detect iPhones as "mac" and push the macOS build).
  const isMobileDevice = /android|iphone|ipad|ipod/i.test(navigator.userAgent);

  useEffect(() => {
    if (isMobileDevice) return;
    // Show at most once per 24h (persisted across sessions/tabs), and ONLY to users
    // who haven't already installed + signed into the desktop app (the backend
    // reports registered=true once a device is linked to this contact).
    const DAY = 24 * 60 * 60 * 1000;
    const last = parseInt(localStorage.getItem("midtc_desktop_nudge_last_shown") || "0", 10);
    if (Date.now() - last < DAY) return;
    getPortalDesktopStatus()
      .then(r => {
        if (!r.data?.registered) {
          // Stamp now so it won't reappear today even if the user ignores it.
          localStorage.setItem("midtc_desktop_nudge_last_shown", String(Date.now()));
          setShow(true);
        }
      })
      .catch(() => {});
  }, [isMobileDevice]);

  if (!show) return null;

  const dismiss = () => setShow(false);

  const DownloadBtn = ({ platform, label, sub, icon, primaryCta }) => (
    <a
      href={`${API_URL}/downloads/${platform}`}
      style={{
        display:"flex", alignItems:"center", gap:12, padding:"14px 16px", borderRadius:10,
        textDecoration:"none", flex:1, minWidth:200,
        background: primaryCta ? primary : "rgba(255,255,255,0.06)",
        border: `1px solid ${primaryCta ? primary : "rgba(255,255,255,0.12)"}`,
      }}
    >
      <div style={{ width:38, height:38, borderRadius:8, flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center",
        background: primaryCta ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.08)" }}>
        {icon}
      </div>
      <div style={{ minWidth:0 }}>
        <div style={{ fontSize:13, fontWeight:700, color:"#fff" }}>{label}</div>
        <div style={{ fontSize:11, color: primaryCta ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.5)" }}>{sub}</div>
      </div>
      <Download size={15} color={primaryCta ? "#fff" : "rgba(255,255,255,0.5)"} style={{ marginLeft:"auto", flexShrink:0 }} />
    </a>
  );

  const winBtn = <DownloadBtn key="win" platform="windows" label="Download for Windows" sub="Windows 10 or later · 64-bit" icon={<MonitorDown size={20} color="#fff" />} primaryCta={os === "windows"} />;
  const macBtn = <DownloadBtn key="mac" platform="mac"     label="Download for macOS"   sub="macOS 10.15 or later"        icon={<Apple size={20} color="#fff" />}        primaryCta={os === "mac"} />;
  // Put the user's detected platform first.
  const buttons = os === "mac" ? [macBtn, winBtn] : [winBtn, macBtn];

  return (
    <div
      onClick={dismiss}
      style={{ position:"fixed", inset:0, background:"rgba(2,6,23,0.72)", backdropFilter:"blur(3px)",
        display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:16 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background:"#111b2f", border:"1px solid rgba(255,255,255,0.1)", borderRadius:16,
          maxWidth:560, width:"100%", padding:"32px 32px 28px", position:"relative",
          boxShadow:"0 24px 60px rgba(0,0,0,0.5)" }}
      >
        <button onClick={dismiss} style={{ position:"absolute", top:16, right:16, background:"none", border:"none",
          color:"rgba(255,255,255,0.4)", cursor:"pointer", padding:4 }}>
          <X size={18} />
        </button>

        <div style={{ width:52, height:52, borderRadius:12, background:`${primary}22`, border:`1px solid ${primary}55`,
          display:"flex", alignItems:"center", justifyContent:"center", marginBottom:18 }}>
          <Monitor size={26} color={primary} />
        </div>

        <h2 style={{ margin:"0 0 8px", fontSize:20, fontWeight:800, color:"#fff" }}>
          Finish setup — install the desktop app
        </h2>
        <p style={{ margin:"0 0 22px", fontSize:14, color:"rgba(255,255,255,0.55)", lineHeight:1.6 }}>
          We don't see the MIDTC desktop app on your computer yet. Download it and sign in with this
          same account — that links your device to your support team so we can help you faster and keep it protected.
        </p>

        <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:18 }}>
          {buttons}
        </div>

        <button onClick={dismiss} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.4)",
          fontSize:12, cursor:"pointer", padding:0 }}>
          I'll do this later
        </button>
      </div>
    </div>
  );
}

export default function PortalApp() {
  const { identifier } = useParams();
  const [branding,  setBranding]  = useState(null);
  const [contact,   setContact]   = useState(null);
  const [loadingB,  setLoadingB]  = useState(true);
  const [notFound,  setNotFound]  = useState(false);

  // Load branding from slug
  useEffect(() => {
    if (!identifier) { setNotFound(true); setLoadingB(false); return; }
    getPortalBranding(identifier)
      .then(r => setBranding(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoadingB(false));
  }, [identifier]);

  // Restore session
  useEffect(() => {
    const stored = localStorage.getItem("midtc_portal_contact");
    if (stored) {
      try {
        setContact(JSON.parse(stored));
        registerMobileDevice(localStorage.getItem("midtc_portal_token")); // phones only — no-op on desktop
      } catch {}
    }
  }, []);

  // If a logged-out visitor lands on a secure-link route, fetch its recipient so the
  // login screen can say "this link is for X" and pre-target the right SSO account.
  const [secureExpected, setSecureExpected] = useState("");
  useEffect(() => {
    const m = window.location.pathname.match(/\/secure\/([^/?#]+)/);
    if (!m) return;
    getSecureLinkMeta(m[1])
      .then(r => { if (r.data?.recipient_email) setSecureExpected(r.data.recipient_email); })
      .catch(() => {});
  }, []);

  const handleLogin  = (c) => {
    setContact(c);
    registerMobileDevice(localStorage.getItem("midtc_portal_token")); // phones only — no-op on desktop
  };
  const handleLogout = () => {
    localStorage.removeItem("midtc_portal_token");
    localStorage.removeItem("midtc_portal_contact");
    setContact(null);
  };

  // Loading branding
  if (loadingB) return (
    <div style={{ minHeight:"100vh", background:"#0f172a", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ width:36, height:36, border:"3px solid #4f6fff", borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.7s linear infinite" }} />
    </div>
  );

  // Invalid slug
  if (notFound) return (
    <div style={{ minHeight:"100vh", background:"var(--t-page, #111b2f)", display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:12, color:"#475569" }}>
      <X size={40} />
      <div style={{ fontSize:16 }}>Portal not found</div>
      <div style={{ fontSize:13 }}>The URL <code style={{ color:"#64748b" }}>/clientportal/{identifier}</code> doesn't match any client portal.</div>
    </div>
  );

  // Not logged in
  if (!contact) return <PortalLogin branding={branding} onLogin={handleLogin} expectedEmail={secureExpected} />;

  // Verify the logged-in contact matches this portal's client
  if (contact.clientId !== branding.client_id) {
    handleLogout();
    return <PortalLogin branding={branding} onLogin={handleLogin} expectedEmail={secureExpected} />;
  }

  // Logged in — render portal with nested routes
  return (
    <PortalLayout branding={branding} contact={contact} onLogout={handleLogout}>
      <DesktopDownloadModal branding={branding} />
      <Routes>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<PortalDashboard branding={branding} contact={contact} />} />
        <Route path="tickets"   element={<PortalTicketList branding={branding} />} />
        <Route path="tickets/new" element={<PortalNewTicket branding={branding} />} />
        <Route path="tickets/:ticketId" element={<PortalTicketDetail branding={branding} />} />
        <Route path="knowledge-base" element={<PortalKnowledgeBase branding={branding} />} />
        {contact.portalRole === "admin" && (
          <Route path="devices" element={<PortalDevices />} />
        )}
        <Route path="secure/:shareToken" element={<SecureLinkPortalView branding={branding} contact={contact} onLogout={handleLogout} />} />
        <Route path="*" element={<Navigate to="dashboard" replace />} />
      </Routes>
    </PortalLayout>
  );
}
